/**
 * The pyramid's lifecycle, as something a person can read.
 *
 * ADR-0006 names this as the price of tiling: *"adding latency between 'architect clicks
 * Publish' and 'drawing is viewable' — which must be handled in the UI as a visible processing
 * state rather than a mysterious delay."* So none of these is a spinner and none is an empty
 * canvas. Each says what is happening and what the person can do instead.
 *
 * **`failed` is not an error screen.** A failed pyramid is not a failed drawing — the PDF still
 * downloads and the record document is intact. `mark_failed` in the backend says the same
 * thing from the other side. The copy follows it.
 */

import { Button } from '@/components';
import { useMessages } from '@/lib/i18n';

import styles from './Viewer.module.css';

export interface RenditionStateProps {
  readonly state: 'absent' | 'queued' | 'running' | 'failed';
  readonly failureCode?: string | null | undefined;
  readonly onBuild?: (() => void) | undefined;
  readonly building?: boolean | undefined;
}

export function RenditionState({ state, failureCode, onBuild, building }: RenditionStateProps) {
  const t = useMessages('drawings');

  if (state === 'absent') {
    return (
      <div className={styles.state}>
        <h2 className={styles.stateTitle}>{t('viewer.absent.title')}</h2>
        <p className={styles.stateBody}>{t('viewer.absent.body')}</p>
        {onBuild === undefined ? null : (
          <Button onClick={onBuild} disabled={building === true}>
            {t('viewer.absent.action')}
          </Button>
        )}
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div className={styles.state}>
        <h2 className={styles.stateTitle}>{t('viewer.failed.title')}</h2>
        <p className={styles.stateBody}>{t('viewer.failed.body')}</p>
        {/* The code, verbatim and unglossed. It is opaque by design — never the parser's own
            message, which is derived from bytes somebody else chose — and it is what makes a
            support conversation about this drawing possible at all. */}
        {failureCode === null || failureCode === undefined ? null : (
          <p className={styles.stateCode}>{failureCode}</p>
        )}
        {onBuild === undefined ? null : (
          <Button variant="secondary" onClick={onBuild} disabled={building === true}>
            {t('viewer.failed.action')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.state}>
      <h2 className={styles.stateTitle}>
        {state === 'queued' ? t('viewer.queued.title') : t('viewer.running.title')}
      </h2>
      <p className={styles.stateBody}>{t('viewer.processing.body')}</p>
    </div>
  );
}
